import { badgeStyle } from "../hrUi";

// Small pure helpers for the payroll screens.

// 9354.8 -> "9,354.80". Money comes from the API as numbers with two decimals.
export function formatMoney(value) {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const CURRENCY = { AE: "AED", IN: "INR" };

export const RUN_STATUS = {
  DRAFT: { label: "Draft", bg: "#F3F4F6", color: "#374151" },
  CALCULATED: { label: "Calculated", bg: "#DBEAFE", color: "#1E40AF" },
  SUBMITTED: { label: "Waiting for approval", bg: "#FEF3C7", color: "#92400E" },
  APPROVED: { label: "Approved", bg: "#D1FAE5", color: "#065F46" },
  LOCKED: { label: "Locked", bg: "#E0E7FF", color: "#3730A3" },
  PUBLISHED: { label: "Published", bg: "#D1FAE5", color: "#065F46" },
  CANCELLED: { label: "Cancelled", bg: "#F3F4F6", color: "#4B5563" },
};
export const runStatusLabel = (s) => (RUN_STATUS[s] ? RUN_STATUS[s].label : s);
export const runStatusBadge = (s) => badgeStyle((RUN_STATUS[s] || RUN_STATUS.DRAFT).bg, (RUN_STATUS[s] || RUN_STATUS.DRAFT).color);

export const RUN_TYPE_LABEL = { REGULAR: "Monthly", OFF_CYCLE: "Off-cycle", FNF: "Full and final" };

// What each step of a run is, in order, for the progress strip.
export const RUN_STEPS = ["DRAFT", "CALCULATED", "SUBMITTED", "APPROVED", "LOCKED", "PUBLISHED"];

// Which buttons a run shows, given its status and what the person may do. One place, so the page and its test agree.
export function runActions(run, { canManage, canApprove }) {
  const s = run.status;
  const out = [];
  if (canManage && ["DRAFT", "CALCULATED"].includes(s)) out.push("calculate");
  if (canManage && s === "CALCULATED" && run.totals && run.totals.employees > 0 && !run.totals.blocking) out.push("submit");
  if (canApprove && s === "SUBMITTED") out.push("approve", "reject");
  if (canManage && ["SUBMITTED", "APPROVED"].includes(s)) out.push("withdraw");
  if (canManage && s === "APPROVED") out.push("lock");
  if (canManage && s === "LOCKED") out.push("publish");
  if (canManage && ["DRAFT", "CALCULATED", "SUBMITTED", "APPROVED"].includes(s)) out.push("cancel");
  return out;
}

export const EXCEPTION_LABEL = {
  NO_SALARY: "No salary",
  NEGATIVE_NET: "Negative net pay",
  NO_BANK: "No bank details",
  VARIANCE: "Big change",
  MISSING_PUNCH: "Missing punch",
  OPEN_ABSENCE: "Absence not sorted out",
  MISSING_RATE: "Rate not set",
  NO_OT_BASE: "No basic for overtime",
  DEDUCTION_LIMIT: "Deductions over the limit",
  ZERO_PAID_DAYS: "No paid days",
  NOTHING_TO_PAY: "Nothing to pay",
  NO_BASIC: "No basic pay",
  NOT_ELIGIBLE: "Not eligible",
  NO_GRATUITY_PACK: "Gratuity not calculated",
};
export const exceptionLabel = (code) => EXCEPTION_LABEL[code] || code;

// "2026-10" for the month a run is being made for: this month, or last month once the month is over.
export function defaultPayMonth(today = new Date()) {
  const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// The register as rows for a spreadsheet: a header row, then one row per person, then the totals.
export function registerSheet(register) {
  const cols = register.columns || [];
  const head = ["Code", "Name", "Paid days", "LOP days", ...cols.map((c) => c.name), "Gross", "Deductions", "Net", "Status"];
  const rows = (register.rows || []).map((r) => [
    r.employee_code,
    r.full_name,
    Number(r.paid_days),
    Number(r.lop_days),
    ...cols.map((c) => Number(r.amounts[c.key] || 0)),
    Number(r.gross),
    Number(r.deductions),
    Number(r.net),
    r.status === "EXCLUDED" ? "Excluded" : r.is_held ? "Held" : "Pay",
  ]);
  const sum = (f) => Math.round(rows.reduce((a, r) => a + f(r), 0) * 100) / 100;
  const n = 4 + cols.length;
  const totals = ["", "Total", "", "", ...cols.map((_, i) => sum((r) => r[4 + i])), sum((r) => r[n]), sum((r) => r[n + 1]), sum((r) => r[n + 2]), ""];
  return [head, ...rows, totals];
}

// The line on a salary revision form that the CTC split leaves over: the sum of FIXED lines, so the form can warn early.
export function fixedTotal(lines) {
  return Math.round((lines || []).filter((l) => l.calc_type === "FIXED").reduce((a, l) => a + (Number(l.amount) || 0), 0) * 100) / 100;
}
